'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { SmartImage } from './smartimage';
import { usePixelatedConfig } from '../config/config.client';
import './recipe.css';
export function mapSchemaRecipeToDisplay(schemaRecipe) {
    // Parse ISO 8601 duration and convert to readable format
    function parseDuration(iso8601) {
        if (!iso8601 || iso8601 === 'PT0M')
            return '';
        const regex = /PT(?:(\d+)H)?(?:(\d+)M)?/;
        const match = iso8601.match(regex);
        if (!match)
            return iso8601;
        const hours = match[1] ? parseInt(match[1]) : 0;
        const minutes = match[2] ? parseInt(match[2]) : 0;
        if (hours > 0 && minutes > 0) {
            return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minutes`;
        }
        else if (hours > 0) {
            return `${hours} hour${hours > 1 ? 's' : ''}`;
        }
        else if (minutes > 0) {
            return `${minutes} minutes`;
        }
        return '';
    }
    // Extract author name
    const authorName = typeof schemaRecipe.author === 'string'
        ? schemaRecipe.author
        : schemaRecipe.author?.name || '';
    // Convert instructions from HowToStep format to plain text
    const instructions = Array.isArray(schemaRecipe.recipeInstructions)
        ? schemaRecipe.recipeInstructions.map((instruction) => typeof instruction === 'string' ? instruction : instruction.text || '')
        : [];
    // Combine cook and prep times for display
    let displayDuration = '';
    if (schemaRecipe.totalTime) {
        displayDuration = parseDuration(schemaRecipe.totalTime);
    }
    return {
        name: schemaRecipe.name || '',
        photo: schemaRecipe.image || '',
        summary: schemaRecipe.description || '',
        author: authorName,
        published: schemaRecipe.datePublished || '',
        duration: displayDuration,
        yield: schemaRecipe.recipeYield || '',
        ingredients: schemaRecipe.recipeIngredient || [],
        instructions,
        category: schemaRecipe.recipeCategory ? [schemaRecipe.recipeCategory] : [],
        license: schemaRecipe.license || ''
    };
}
/* ========== RECIPE BOOK ========== */
/**
 * RecipeBook — renders a browsable collection of recipes grouped by category with deep-linking support.
 *
 * @param {shape} [props.recipeData] - Object containing recipe items in schema.org/Recipe format.
 * @param {array} [props.items] - Array of recipe items (used internally by recipeData.items).
 * @param {array} [props.recipeCategories] - Array of category names used to group and render recipes.
 */
RecipeBook.propTypes = {
    /** Object containing recipe items (schema.org/Recipe objects). */
    recipeData: PropTypes.shape({
        /** Array of recipe items used to render the book. */
        items: PropTypes.array.isRequired
    }).isRequired,
    /** Categories used to group recipes in the book (array of strings). */
    recipeCategories: PropTypes.array.isRequired
};
export function RecipeBook(props) {
    const [recipeElems] = useState(generateMyElems());
    const [outputElems, setOutputElems] = useState([]);
    const [showOnlyCat, setShowOnlyCat] = useState('');
    const [showOnlyRecipe, setShowOnlyRecipe] = useState('');
    function generateMyElems() {
        const myElems = [];
        const recipeBookItems = props.recipeData.items;
        for (const catKey in props.recipeCategories) {
            const category = props.recipeCategories[catKey];
            myElems[category] = [];
            for (const recipeKey in recipeBookItems) {
                const recipe = recipeBookItems[recipeKey];
                const outputRecipe = mapSchemaRecipeToDisplay(recipe);
                const recipeCat = outputRecipe.category;
                if (recipeCat.includes(category)) {
                    myElems[category].push(outputRecipe);
                }
            }
        }
        return myElems;
    }
    function outputMyElems() {
        const myElems = [];
        let catKey = 1;
        for (const category in recipeElems) {
            const cID = 'c' + (catKey);
            myElems.push(_jsx(RecipeCategory, { id: cID, className: 'h-recipe-category', category: category, showOnly: showOnlyCat }, cID));
            for (const recipeKey in recipeElems[category]) {
                const recipe = recipeElems[category][recipeKey];
                const cats = recipe.category;
                const rID = cID + '-r' + (parseInt(recipeKey, 10) + 1);
                if (cats.includes(category)) {
                    myElems.push(_jsx(RecipeBookItem, { id: rID, recipeData: recipe, showOnly: showOnlyRecipe }, rID));
                }
            }
            catKey += 1;
        }
        return myElems;
    }
    useEffect(() => {
        setOutputElems(outputMyElems());
    }, [showOnlyCat, showOnlyRecipe]);
    // Deep linking: read URL hash on mount and select recipe if present
    useEffect(() => {
        const hash = window.location.hash.replace('#', '');
        if (hash && hash.length > 0) {
            onRecipePickListChange(hash);
        }
    }, []); // Empty dependency array - only run on mount
    function onRecipePickListChange(optionVal) {
        let cID, rID;
        if (optionVal.includes('-')) {
            cID = optionVal.substring(0, optionVal.indexOf('-'));
            rID = optionVal;
        }
        else {
            cID = optionVal;
            rID = optionVal;
        }
        setShowOnlyCat(cID);
        setShowOnlyRecipe(rID);
        setOutputElems(outputMyElems());
        window.location.hash = rID;
    }
    return (_jsxs("div", { id: "recipes", children: [_jsx(BackToTop, {}), _jsx(RecipePickList, { recipeData: props.recipeData, recipeCategories: props.recipeCategories, handleRecipePickListChange: onRecipePickListChange }), outputElems] }));
}
/* ========== RECIPE CATEGORY ========== */
/**
 * RecipeCategory — renders a category heading for a group of recipes.
 *
 * @param {string} [props.id] - DOM id for the category heading element.
 * @param {string} [props.className] - CSS class names for the heading element.
 * @param {string} [props.category] - Category name displayed to users.
 * @param {string} [props.showOnly] - Optional filter key used to hide/show categories via deep link or selection.
 */
RecipeCategory.propTypes = {
    /** DOM id for the category heading. */
    id: PropTypes.string.isRequired,
    /** CSS class names to apply to the heading. */
    className: PropTypes.string.isRequired,
    /** Category display name. */
    category: PropTypes.string.isRequired,
    /** Filter token used to show only a specific recipe or category. */
    showOnly: PropTypes.string.isRequired
};
export function RecipeCategory(props) {
    const isHidden = ((props.showOnly.length > 0) && (!(props.id.includes(props.showOnly)))
        ? { display: 'none' }
        : { display: 'initial' });
    return (_jsx("h2", { id: props.id, className: props.className, style: isHidden, children: props.category }));
}
/* ========== RECIPE ========== */
/**
 * RecipeBookItem — displays a single recipe including image, ingredients and instructions.
 *
 * @param {object} [props.recipeData] - Normalized recipe data used for rendering (name, photo, ingredients, instructions, etc.).
 * @param {string} [props.id] - DOM id for deep-linking to this recipe.
 * @param {string} [props.showOnly] - Filter token used to hide/show the item for deep-linking.
 */
RecipeBookItem.propTypes = {
    /** Normalized recipe data object used to render the recipe view. */
    recipeData: PropTypes.object.isRequired,
    /** DOM id used for deep-linking. */
    id: PropTypes.string.isRequired,
    /** Filter token used when deep-linking or scoping displayed recipes. */
    showOnly: PropTypes.string.isRequired
};
export function RecipeBookItem(props) {
    const config = usePixelatedConfig();
    const recipe = props.recipeData;
    const ingredients = recipe.ingredients.map((ingredient, iKey) => _jsx("li", { className: "p-ingredient", children: ingredient }, iKey));
    const instructions = recipe.instructions.map((instruction, iKey) => _jsx("li", { className: "p-instruction", children: instruction }, iKey));
    const recipeImage = (recipe.photo.length > 0
        ? _jsx(SmartImage, { className: 'u-photo', src: recipe.photo, title: recipe.name, alt: recipe.name, cloudinaryEnv: config?.integrations?.cloudinary?.product_env ?? undefined, cloudinaryDomain: config?.integrations?.cloudinary?.baseUrl ?? undefined, cloudinaryTransforms: config?.integrations?.cloudinary?.transforms ?? undefined })
        : null);
    const isHidden = ((props.showOnly.length > 0) && (!(props.id.includes(props.showOnly))) ? { display: 'none' } : { display: 'initial' });
    /* event.preventDefault(); */
    return (_jsxs("article", { id: props.id, className: "h-recipe", style: isHidden, children: [_jsx("h3", { className: "p-name", children: _jsx("a", { id: props.id, href: `#${props.id}`, onClick: () => { return false; }, children: recipe.name }) }), recipeImage, _jsx("p", { className: "p-summary", children: recipe.summary }), _jsx("p", { children: "\u00A0" }), _jsxs("p", { className: "p-author", children: ["Author: ", recipe.author] }), _jsxs("p", { className: "p-published", children: ["Published: ", recipe.published] }), _jsxs("p", { className: "dt-duration", children: ["Duration: ", recipe.duration] }), _jsxs("p", { className: "p-yield", children: ["Yield: ", recipe.yield] }), _jsx("h4", { className: "e-ingredients", children: "Ingredients" }), _jsx("ul", { children: ingredients }), _jsx("h4", { className: "e-instructions", children: "Instructions" }), _jsx("ol", { children: instructions })] }));
}
/* ========== RECIPE PICK LIST ========== */
/**
 * RecipePickList — renders a select control for picking recipes by category and recipe name.
 *
 * @param {object} [props.recipeData] - Object containing recipe items accessible by category.
 * @param {array} [props.recipeCategories] - Categories used to organize recipes in the select list.
 * @param {function} [props.handleRecipePickListChange] - Callback invoked when the selected recipe changes (receives selected id).
 */
RecipePickList.propTypes = {
    /** Recipe data object containing items used to build the pick list. */
    recipeData: PropTypes.object.isRequired,
    /** Array of category names for grouping recipes in the select. */
    recipeCategories: PropTypes.array.isRequired,
    /** Change handler called with the selected recipe identifier when the pick list changes. */
    handleRecipePickListChange: PropTypes.func.isRequired
};
export function RecipePickList(props) {
    const [recipeOptions, setRecipeOptions] = useState([]);
    function generateMyOptions() {
        const myOpts = [];
        myOpts.push(_jsx("option", { value: '', children: "Choose a recipe below:" }, 'x0'));
        for (const catKey in props.recipeCategories) {
            const category = props.recipeCategories[catKey];
            const cID = 'c' + (parseInt(catKey, 10) + 1);
            myOpts.push(_jsxs("option", { value: cID, children: ["=== ", category.toUpperCase(), " ==="] }, cID));
            let rID = 1;
            const recipeData = props.recipeData;
            const recipeDataItems = recipeData.items;
            for (const recipeKey in recipeDataItems) {
                const recipe = recipeDataItems[recipeKey];
                const outputRecipe = mapSchemaRecipeToDisplay(recipe);
                const cats = outputRecipe.category;
                if (cats.includes(category)) {
                    myOpts.push(_jsx("option", { value: cID + '-r' + rID, children: outputRecipe.name }, cID + '-r' + rID));
                    rID += 1;
                }
            }
        }
        return myOpts;
    }
    function recipeListChanged(e) {
        if (e.target.value.length > 0) {
            props.handleRecipePickListChange(e.target.value);
        }
        else {
            props.handleRecipePickListChange('');
        }
    }
    useEffect(() => {
        setRecipeOptions(generateMyOptions());
    }, [props.recipeData, props.recipeCategories]);
    return (_jsx("form", { children: _jsx("select", { id: "recipe-list", name: "recipe-list", onChange: recipeListChanged, children: recipeOptions }) }));
}
/* ========== RECIPE BACK TO TOP ========== */
/** BackToTop.propTypes — No props (scroll-to-top control).
 * @param {any} [props] - No props are accepted by BackToTop.
 */
BackToTop.propTypes = { /** no props */};
export function BackToTop() {
    function scrollToTop() {
        window.scroll({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
        return false;
    }
    const config = usePixelatedConfig();
    return (_jsx("div", { className: "back-to-top", children: _jsxs("a", { href: "#top", onClick: scrollToTop, children: [_jsx("div", { children: _jsx(SmartImage, { src: "https://www.pixelated.tech/images/icons/up.jpg", title: "Back To Top", alt: "Back To Top", cloudinaryEnv: config?.integrations?.cloudinary?.product_env ?? undefined, cloudinaryDomain: config?.integrations?.cloudinary?.baseUrl ?? undefined, cloudinaryTransforms: config?.integrations?.cloudinary?.transforms ?? undefined }) }), _jsx("div", { children: "Back To Top" })] }) }));
}
